// src/features/hoa/constants.js
//
// Display labels, icons, and enum values for the HOA module.
// Icons use lucide-react (same as rest of web app).
//
// COLOR RULES — brand v2:
//   All `color` values map to the brand v2 semantic palette (red / green /
//   amber / blue / orange / gray). NEVER use 'purple' here — purple is
//   exclusively reserved for the Interior Design vertical.

import {
  AlertTriangle,
  Leaf,
  KeyRound,
  Calendar,
  MessageSquare,
  CreditCard,
  Home,
  FileCheck,
  HelpCircle,
  // Expenditure category icons
  Sparkles,
  Hammer,
  Waves,
  FileText,
  Shield,
  Lock,
  Package,
  // Admin sub-nav icons
  LayoutDashboard,
  Inbox,
  User,
  Building2,
} from 'lucide-react';

// ─── Request categories ──────────────────────────────────────────────
export const REQUEST_CATEGORIES = [
  {
    id: 'incident',
    label: 'Incident',
    description: 'Report a problem in the community (noise, safety, damage)',
    icon: AlertTriangle,
    color: 'red',
  },
  {
    id: 'landscaping',
    label: 'Landscaping',
    description: 'Tree trimming, irrigation, or yard work',
    icon: Leaf,
    color: 'green',
  },
  {
    id: 'access',
    label: 'Access',
    description: 'Gate codes, amenity booking, or visitor passes',
    icon: KeyRound,
    color: 'blue',
  },
  {
    id: 'building_permit',
    label: 'Building Permit',
    description: 'Request approval for additions, paint, fences, or exterior changes',
    icon: FileCheck,
    color: 'amber',
  },
  {
    id: 'general',
    label: 'General',
    description: 'Anything else that doesn\'t fit the categories above',
    icon: HelpCircle,
    color: 'gray',
  },
];

export const REQUEST_CATEGORY_MAP = Object.fromEntries(
  REQUEST_CATEGORIES.map((c) => [c.id, c])
);

// ─── Request status ──────────────────────────────────────────────────
export const REQUEST_STATUSES = [
  { id: 'open',        label: 'Open',        color: 'blue'  },
  { id: 'in_progress', label: 'In Progress', color: 'amber' },
  { id: 'resolved',    label: 'Resolved',    color: 'green' },
  { id: 'closed',      label: 'Closed',      color: 'gray'  },
];

export const REQUEST_STATUS_MAP = Object.fromEntries(
  REQUEST_STATUSES.map((s) => [s.id, s])
);

// ─── Announcement priority ───────────────────────────────────────────
export const ANNOUNCEMENT_PRIORITIES = [
  { id: 'info',     label: 'Info',     color: 'gray'  },
  { id: 'reminder', label: 'Reminder', color: 'blue'  },
  { id: 'alert',    label: 'Alert',    color: 'red'   },
];

export const ANNOUNCEMENT_PRIORITY_MAP = Object.fromEntries(
  ANNOUNCEMENT_PRIORITIES.map((p) => [p.id, p])
);

// ─── Billing / invoice status ────────────────────────────────────────
export const INVOICE_STATUSES = [
  { id: 'paid',      label: 'Paid',      color: 'green' },
  { id: 'due',       label: 'Due',       color: 'amber' },
  { id: 'overdue',   label: 'Overdue',   color: 'red'   },
  { id: 'scheduled', label: 'Scheduled', color: 'gray'  },
];

export const INVOICE_STATUS_MAP = Object.fromEntries(
  INVOICE_STATUSES.map((s) => [s.id, s])
);

// ─── Community budget expenditure categories ─────────────────────────
export const EXPENDITURE_CATEGORIES = [
  {
    id: 'events',
    label: 'Events & gatherings',
    shortLabel: 'Events',
    icon: Sparkles,
    color: 'orange',
  },
  {
    id: 'landscaping',
    label: 'Landscaping',
    shortLabel: 'Landscaping',
    icon: Leaf,
    color: 'green',
  },
  {
    id: 'maintenance',
    label: 'Maintenance',
    shortLabel: 'Maintenance',
    icon: Hammer,
    color: 'amber',
  },
  {
    id: 'amenities',
    label: 'Amenities',
    shortLabel: 'Amenities',
    icon: Waves,
    color: 'blue',
  },
  {
    id: 'admin',
    label: 'Admin & communications',
    shortLabel: 'Admin',
    icon: FileText,
    color: 'gray',
  },
  {
    id: 'insurance',
    label: 'Insurance',
    shortLabel: 'Insurance',
    icon: Shield,
    color: 'blue',
  },
  {
    id: 'security',
    label: 'Security',
    shortLabel: 'Security',
    icon: Lock,
    color: 'red',
  },
  {
    id: 'other',
    label: 'Other',
    shortLabel: 'Other',
    icon: Package,
    color: 'gray',
  },
];

export const EXPENDITURE_CATEGORY_MAP = Object.fromEntries(
  EXPENDITURE_CATEGORIES.map((c) => [c.id, c])
);

// ─── Member roles ────────────────────────────────────────────────────
// Canonical enum for role values stored on HoaMember records.
export const MEMBER_ROLES = {
  RESIDENT:                 'resident',
  BOARD_ADMIN:              'board_admin',
  MANAGEMENT_COMPANY_ADMIN: 'management_company_admin',
  HOMECIERGE_STAFF:         'homecierge_staff',
};

// ─── Invite status ───────────────────────────────────────────────────
export const INVITE_STATUSES = {
  PENDING:  'pending',
  ACCEPTED: 'accepted',
  EXPIRED:  'expired',
  REVOKED:  'revoked',
};

// ─── Admin modes (dev-only mock toggle) ──────────────────────────────
// Drives the AdminModeToggle in the sidebar. Only meaningful while
// USE_MOCKS = true. Values align with HoaMemberRole enum so hooks and
// permission helpers work uniformly.
export const ADMIN_MODES = [
  {
    id: 'resident',
    label: 'Resident',
    shortLabel: 'Resident',
    description: "See the app as a regular resident.",
    icon: User,
  },
  {
    id: 'board_admin',
    label: 'Board Admin',
    shortLabel: 'Board',
    description: 'Elected board member — triage, events, announcements.',
    icon: Shield,
  },
  {
    id: 'management_company_admin',
    label: 'Mgmt Co Admin',
    shortLabel: 'Mgmt Co',
    description: 'Essex Management employee — full permissions + billing.',
    icon: Building2,
  },
];

export const ADMIN_MODE_MAP = Object.fromEntries(
  ADMIN_MODES.map((m) => [m.id, m])
);

export const DEFAULT_ADMIN_MODE = 'resident';

// ─── HOA sub-nav (resident view) ─────────────────────────────────────
export const HOA_SUBNAV = [
  { id: 'overview', name: 'Overview',       path: '/hoa',          icon: Home          },
  { id: 'events',   name: 'Events',         path: '/hoa/events',   icon: Calendar      },
  { id: 'chat',     name: 'Community Chat', path: '/hoa/chat',     icon: MessageSquare },
  { id: 'requests', name: 'Requests',       path: '/hoa/requests', icon: AlertTriangle },
  { id: 'billing',  name: 'Dues & Billing', path: '/hoa/billing',  icon: CreditCard    },
];

// ─── HOA Admin sub-nav (admin view — PR B will activate) ─────────────
// Defined in PR A so PR B can reference it when it adds the sidebar
// tree expansion for admin users. Not rendered anywhere in PR A.
export const HOA_ADMIN_SUBNAV = [
  { id: 'admin-dashboard', name: 'Admin Dashboard', path: '/hoa-admin',          icon: LayoutDashboard },
  { id: 'admin-requests',  name: 'Triage Queue',    path: '/hoa-admin/requests', icon: Inbox           },
];
